import { useQuery } from '@tanstack/react-query';

import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { getShortProjects } from '@/modules/projects/api/short-projects.api';
import { NON_ARCHIVED_PROJECT_STATUSES } from '@/modules/projects/types/dto/request/find-short-projects-request';

import { ACTIVE_TIMER_QUERY_KEYS } from '../constants/active-timer-query-keys';
import { useTimeTrackingStore } from '../store/time-tracking.store';

/**
 * Resolves the running timer's project (name + color) for the widget.
 *
 * The previous implementation fetched `/api/projects/one/full`, which 403s for
 * worklog-only users — wiping the project from the widget after the timer starts.
 * We never need full project details here, only id/name/color, so:
 *
 * 1. Prefer the project the user selected, persisted in the store at timer start
 *    (zero network calls — covers the common case).
 * 2. When that's missing (e.g. a timer started on another device, found running
 *    on load), fall back to the permission-safe `/projects/short/search` and pick
 *    the matching id. If it isn't on the first page the dot stays blank; the timer
 *    still works and the name resolves once the project is selected/interacted with.
 */
export const useActiveTimerProject = (
  projectId: number | null | undefined
): DropdownSearchItem | undefined => {
  const storedProject = useTimeTrackingStore(state => state.activeTimerProject);

  const hasStoredMatch = storedProject != null && Number(storedProject.id) === projectId;

  // Only hit the network when the timer has a project we can't resolve locally.
  const shouldLookup = projectId != null && !hasStoredMatch;

  const { data: lookedUpProject } = useQuery({
    queryKey: ACTIVE_TIMER_QUERY_KEYS.project(projectId ?? null),
    queryFn: async () => {
      const page = await getShortProjects({
        filter: { status: NON_ARCHIVED_PROJECT_STATUSES },
      });
      return page.items.find(item => Number(item.id) === projectId) ?? null;
    },
    enabled: shouldLookup,
    staleTime: 5 * 60 * 1000,
  });

  if (hasStoredMatch) return storedProject;
  return lookedUpProject ?? undefined;
};
